import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MessageService } from '@ebootis/utilities';
import { TranslateService } from '@ngx-translate/core';
import { OrderDetailsByOid } from 'projects/zbs-app/src/app/services/Ebootis.Queries/OrderDetailsByOid';
import { IZbsApp } from 'projects/zbs-app/src/app/services/Ebootis.ServiceProxies/IZbsApp';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { PrintService } from '../../../print/print.service';
import { OrderService } from '../../order.service';
import { OrderPrintComponent } from '../order-print/order-print.component';

interface AuthorizeOrderItemForm {
	itemOid: FormControl<string>;
	amount: FormControl<number>;
	needsAuthorization: FormControl<boolean>;
}
interface AuthorizeOrderParamsForm {
	items: FormArray<FormGroup<AuthorizeOrderItemForm>>;
}

@Component({
	selector: 'app-order-detail',
	templateUrl: './order-detail.component.html',
	styleUrls: ['./order-detail.component.scss']
})
export class OrderDetailComponent implements OnDestroy {

	@Input() set selectedOrder(p_order: string) {
		this.m_selectedOrder = p_order;

		this.m_orderService.getOrderDetailsByOid({ order: this.m_selectedOrder }).pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (res) => {
				this.m_queryData = res;
				this.m_dataSource = new MatTableDataSource(this.m_queryData.items);
				for (let item of this.m_queryData.items)
					this.addAuthorizeOrderItem(item);
			},
			error: (error) => {
				this.m_displayError = error;
				this.m_messageHandlerClass = 'danger';
			}
		});
	}

	@Input() set isEmbedded(p_isEmbedded: boolean) {
		if (p_isEmbedded) {
			this.m_isEmbedded = p_isEmbedded;
		}
	}

	@Output() public exit: EventEmitter<boolean> = new EventEmitter();

	@ViewChild('m_orderPrintComponent') public m_orderPrintComponent: OrderPrintComponent;

	private m_selectedOrder: string = '';
	private m_subscriptionsSubject = new Subject();
	public m_isEmbedded: boolean = false;
	public m_displayError: HttpErrorResponse;
	public m_messageHandlerClass: string = 'info';
	public m_queryData: OrderDetailsByOid.Result;
	public m_dataSource: MatTableDataSource<OrderDetailsByOid.Result>;
	public m_displayedColumns: string[] = ['id', 'name', 'logistic', 'price', 'amount', 'quantityDelivered', 'quantityRemaining', 'unit', 'value'];
	public m_isPrinting: boolean = false;

	public m_orderItemsForm: FormGroup<AuthorizeOrderParamsForm> = this.m_fb.group({
		items: this.m_fb.array<FormGroup<AuthorizeOrderItemForm>>([]),
	});
	public m_authorizeOrderClicked: boolean = false;

	public readonly TX_STATE_CANCELLED: number = 600;

	constructor(
		private m_orderService: OrderService,
		private m_printService: PrintService,
		private m_fb: FormBuilder,
		private m_messageService: MessageService,
		private m_translateService: TranslateService
	) {
	}

	/**
	 * Die Subscriptions werden geschlossen.
	 */
	public ngOnDestroy(): void {
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	public print_Click(): void {
		// Spinner anzeigen, solange der Drucken-Dialog geöffnet ist
		this.m_printService.isPrinting.subscribe({
			next: (isPrinting) => {
				this.m_isPrinting = isPrinting;
			}
		});
		// Drucken-Dialog öffnen
		this.m_printService.printDocument('bestellung', this.m_selectedOrder);
	}

	/**
	 * Die Nachrichten des MessageHandlers werden zurückgesetzt.
	 */
	public clearDisplayMessages(): void {
		this.m_displayError = null;
	}

	/**
	 * setzt eine anzuzeigende Bezeichnung
	 * für die LogisticsArea zusammen
	 * (falls vorhanden).
	 * @param p_element 
	 */
	public getLogisticsAreaString(p_element): string {
		let str = '';
		if (p_element.logisticArea) {
			str = p_element.logisticArea.name;

			if (p_element.logisticArea.descr) {
				str += "- " + p_element.logisticArea.descr;
			}
		}
		return str;
	}

	/**
	 * Boolean wird an aufrufendes Component zurückgegeben. Tab wird gewechselt.
	 */
	public tabBack(): void {
		this.exit.emit(true);
	}

	public anyPositionsNeedsAuthorization(): boolean {
		return this.m_queryData.items.filter((p_item) => p_item.needsAuthorization)?.length > 0;
	}

	private createAuthorizeOrderItem(p_item: OrderDetailsByOid.Item): FormGroup<AuthorizeOrderItemForm> {
		return this.m_fb.group({
			itemOid: [p_item.orderOid, Validators.required],
			amount: [p_item.quantityOrdered, [Validators.required, Validators.min(0), Validators.max(p_item.quantityOrdered)]],
			needsAuthorization: [p_item.needsAuthorization]
		});
	}
	private addAuthorizeOrderItem(p_item: OrderDetailsByOid.Item): void {
		this.m_orderItemsForm.controls.items.push(this.createAuthorizeOrderItem(p_item));
	}

	public authorizeOrder(): void {
		if (!this.anyPositionsNeedsAuthorization())
			return;
		this.m_authorizeOrderClicked = true;

		const items: IZbsApp.AuthorizeOrderItem[] = [];

		for (let i = 0; i < this.m_queryData.items.length; i++) {
			const item: FormGroup<AuthorizeOrderItemForm> = this.m_orderItemsForm.controls.items.at(i);
			if (item.controls.needsAuthorization.value) {
				items.push({
					itemOid: item.controls.itemOid.value,
					amount: item.controls.amount.value
				});
			}
		}

		const params: IZbsApp.AuthorizeOrderParms = {
			orderOid: this.m_queryData.oid,
			items: items
		};

		this.m_orderService.authorizeOrder(params)
			.pipe(take(1)).subscribe({
				next: (data) => {
					this.selectedOrder = this.m_queryData.oid;
					this.m_authorizeOrderClicked = false;
					this.m_messageService.success(this.m_translateService.instant('GLOBAL.BUTTON.AUTHORIZE.SUCCESS', { txId: this.m_queryData.id }));
				},
				error: (error) => {
					this.m_messageHandlerClass = 'danger';
					this.m_displayError = error;
					this.m_authorizeOrderClicked = false;
				},
			});
	}
}
